import React, { useState } from "react";
import { Autocomplete, IconButton, TextField } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { formatCurrency } from "../invoiceFormatting";
import { InvoiceMoneyTextField } from "../InvoiceMockComponents";
import {
  invoiceDesignFontFamily,
  invoiceDesignTokens,
  invoiceFieldSx,
} from "../InvoiceDesignSystem";
import InvoiceLineItemDeleteDialog from "../InvoiceLineItemDeleteDialog";
import InvoiceSearchableSelect from "../InvoiceSearchableSelect";
import { MockInvoiceLineItem } from "../mockInvoiceData";
import {
  applyCatalogOptionToLineItem,
  createEmptyLineItem,
  CUSTOM_LINE_OPTION_ID,
  InvoiceLineItemCatalogOption,
  isCustomLineItem,
} from "./createInvoiceHelpers";

const headerCellClass =
  "px-[14px] py-[11px] text-[12px] font-bold uppercase tracking-[0.04em] text-white whitespace-nowrap";

const lineItemFieldSx = {
  ...invoiceFieldSx,
  "& .MuiOutlinedInput-root": { backgroundColor: "white" },
};

export type LineItemFieldErrors = {
  code?: string;
  description?: string;
  quantity?: string;
  unitPrice?: string;
};

// The Create Invoice line-item table. Account and tax values stay internal and
// come from Chamber Setup. Staff cannot override them from this page.
export const InvoiceLineItemsTable = ({
  accountNumber,
  allowCustomDescription,
  catalogOptions,
  defaultTaxRatePercentage,
  lineItems,
  onChange,
  lineErrors,
  tableError,
}: {
  accountNumber: string;
  allowCustomDescription: boolean;
  catalogOptions: InvoiceLineItemCatalogOption[];
  defaultTaxRatePercentage: number;
  lineItems: MockInvoiceLineItem[];
  onChange: (lineItems: MockInvoiceLineItem[]) => void;
  /** POL-019 (B5): per-line field errors keyed by line item id. */
  lineErrors?: Record<string, LineItemFieldErrors>;
  tableError?: string;
}) => {
  const [lineIdPendingDelete, setLineIdPendingDelete] = useState<string | null>(
    null,
  );
  const hasCatalogOptions = catalogOptions.length > 0;

  const updateLineItem = (id: string, next: Partial<MockInvoiceLineItem>) =>
    onChange(
      lineItems.map((item) => (item.id === id ? { ...item, ...next } : item)),
    );

  const handleSelectOption = (id: string, optionId: string) =>
    onChange(
      lineItems.map((item) =>
        item.id === id
          ? applyCatalogOptionToLineItem({
              lineItem: item,
              optionId,
              options: catalogOptions,
            })
          : item,
      ),
    );
  const handleAddLineItem = () =>
    onChange([
      ...lineItems,
      createEmptyLineItem({
        accountNumber,
        defaultTaxRatePercentage,
      }),
    ]);

  return (
    <div data-invoice-field-error={tableError ? "true" : undefined}>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1000px] border-collapse text-left">
          <thead>
            <tr className="bg-[#0B9DF2]">
              <th className={`${headerCellClass} w-[14%] rounded-tl`}>Code</th>
              <th className={`${headerCellClass} w-[36%]`}>Description</th>
              <th className={`${headerCellClass} w-[10%] text-center`}>Qty</th>
              <th className={`${headerCellClass} w-[16%]`}>Unit Price</th>
              <th className={`${headerCellClass} w-[16%]`}>Sub-total</th>
              <th className={headerCellClass} aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {lineItems.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-6 text-center text-sm text-gray-500"
                >
                  No line items yet. Add one to begin.
                </td>
              </tr>
            ) : null}
            {lineItems.map((item) => {
              const isCustom = isCustomLineItem(item) || !hasCatalogOptions;
              const lineTotalInCents = item.quantity * item.unitPriceInCents;
              const itemErrors = lineErrors?.[item.id];
              const selectedCatalogOption =
                catalogOptions.find(
                  (option) => option.id === item.catalogOptionId,
                ) ||
                (item.catalogOptionId && item.description
                  ? {
                      code: item.code,
                      id: item.catalogOptionId,
                      name: item.description,
                      priceInCents: item.unitPriceInCents,
                    }
                  : null);

              return (
                <tr
                  key={item.id}
                  className="border-b border-[#ECECEC] align-middle"
                >
                  <td
                    className="px-0 py-[14px] pr-3"
                    data-invoice-field-error={
                      itemErrors?.code ? "true" : undefined
                    }
                  >
                    {/* Design hasCode: a catalog option fills and locks its
                        code; an option without a code leaves it editable. */}
                    <TextField
                      placeholder="Enter Code"
                      value={item.code || ""}
                      sx={lineItemFieldSx}
                      disabled={
                        !isCustom &&
                        Boolean(item.catalogOptionId) &&
                        Boolean(selectedCatalogOption?.code)
                      }
                      error={Boolean(itemErrors?.code)}
                      helperText={itemErrors?.code}
                      onChange={({ target }) =>
                        updateLineItem(item.id, { code: target.value })
                      }
                      fullWidth
                    />
                  </td>
                  <td
                    className="min-w-[260px] px-0 py-[14px] pr-3"
                    data-invoice-field-error={
                      itemErrors?.description ? "true" : undefined
                    }
                  >
                    {allowCustomDescription ? (
                      <Autocomplete
                        freeSolo
                        options={catalogOptions}
                        componentsProps={{
                          paper: {
                            sx: {
                              border: "1px solid #9E9E9E",
                              borderRadius: "8px",
                              boxShadow: "0 8px 20px rgba(0,0,0,0.10)",
                              fontFamily: invoiceDesignFontFamily,
                              mt: "4px",
                            },
                          },
                        }}
                        ListboxProps={{
                          sx: {
                            py: 0,
                            "& .MuiAutocomplete-option": {
                              boxSizing: "border-box",
                              fontFamily: invoiceDesignFontFamily,
                              fontSize: 14,
                              lineHeight: "20px",
                              minHeight: 41,
                              px: "12px",
                              py: "10px",
                              "&.Mui-focused, &[aria-selected='true']": {
                                backgroundColor: "#F7F7F8",
                              },
                            },
                          },
                        }}
                        getOptionLabel={(option) =>
                          typeof option === "string" ? option : option.name
                        }
                        isOptionEqualToValue={(option, value) =>
                          typeof value !== "string" && option.id === value.id
                        }
                        value={
                          catalogOptions.find(
                            (option) => option.id === item.catalogOptionId,
                          ) || item.description
                        }
                        onChange={(_event, value) => {
                          if (typeof value === "string") {
                            updateLineItem(item.id, {
                              catalogOptionId: CUSTOM_LINE_OPTION_ID,
                              description: value,
                            });
                          } else if (value) {
                            handleSelectOption(item.id, value.id);
                          } else {
                            updateLineItem(item.id, {
                              catalogOptionId: "",
                              description: "",
                            });
                          }
                        }}
                        onInputChange={(_event, value, reason) => {
                          if (reason === "input") {
                            updateLineItem(item.id, {
                              catalogOptionId: CUSTOM_LINE_OPTION_ID,
                              description: value,
                            });
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Enter Description"
                            sx={lineItemFieldSx}
                            error={Boolean(itemErrors?.description)}
                            helperText={itemErrors?.description}
                          />
                        )}
                      />
                    ) : (
                      <InvoiceSearchableSelect
                        error={Boolean(itemErrors?.description)}
                        getOptionKey={(option) => option.id}
                        getOptionLabel={(option) => option.name}
                        helperText={itemErrors?.description}
                        id={`invoice-line-description-${item.id}`}
                        noOptionsText="No advertising options found"
                        onChange={(option) => {
                          if (option) {
                            handleSelectOption(item.id, option.id);
                            return;
                          }
                          updateLineItem(item.id, {
                            catalogOptionId: "",
                            description: "",
                          });
                        }}
                        options={catalogOptions}
                        placeholder="Enter Description"
                        searchPlaceholder="Search Description"
                        value={selectedCatalogOption}
                      />
                    )}
                  </td>
                  <td
                    className="px-0 py-[14px] pr-3"
                    data-invoice-field-error={
                      itemErrors?.quantity ? "true" : undefined
                    }
                  >
                    <TextField
                      type="number"
                      value={item.quantity}
                      error={Boolean(itemErrors?.quantity)}
                      helperText={itemErrors?.quantity}
                      sx={{
                        ...lineItemFieldSx,
                        "& input": { textAlign: "center" },
                      }}
                      inputProps={{ min: 1, step: 1 }}
                      onChange={({ target }) =>
                        updateLineItem(item.id, {
                          quantity: Math.max(1, Number(target.value) || 1),
                        })
                      }
                      fullWidth
                    />
                  </td>
                  <td
                    className="px-0 py-[14px] pr-3"
                    data-invoice-field-error={
                      itemErrors?.unitPrice ? "true" : undefined
                    }
                  >
                    {/* POL-005 (B5): decimal text input (no spinners) with a
                        focus/blur draft model; storage stays integer cents. */}
                    <InvoiceMoneyTextField
                      valueInCents={item.unitPriceInCents}
                      sx={lineItemFieldSx}
                      ariaLabel="Unit price"
                      error={Boolean(itemErrors?.unitPrice)}
                      helperText={itemErrors?.unitPrice}
                      onValueInCentsChange={(valueInCents) =>
                        updateLineItem(item.id, {
                          unitPriceInCents: valueInCents,
                        })
                      }
                    />
                  </td>
                  <td className="px-3 py-[14px] text-left text-[15px] font-bold text-gray-900">
                    {formatCurrency(lineTotalInCents)}
                  </td>
                  <td className="w-[46px] px-0 py-[14px] text-center">
                    <IconButton
                      aria-label="Remove line item"
                      size="small"
                      onClick={() => setLineIdPendingDelete(item.id)}
                      sx={{
                        backgroundColor: invoiceDesignTokens.closeRed,
                        borderRadius: "6px",
                        color: "white",
                        height: 30,
                        width: 34,
                        "&:hover": { backgroundColor: "#D90F1A" },
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {tableError ? (
        <p className="mt-2 text-xs text-[#E24C4C]">{tableError}</p>
      ) : null}
      <div className="mt-5 flex justify-end">
        <button
          type="button"
          onClick={handleAddLineItem}
          className="inline-flex min-h-[42px] items-center gap-2 rounded-lg bg-[#E1A731] px-[18px] py-[11px] text-sm font-bold text-white transition hover:bg-[#C6912A]"
        >
          <AddIcon sx={{ fontSize: 18 }} /> Add Line Item
        </button>
      </div>
      <InvoiceLineItemDeleteDialog
        onCancel={() => setLineIdPendingDelete(null)}
        onConfirm={() => {
          if (lineIdPendingDelete) {
            onChange(
              lineItems.filter(
                (current) => current.id !== lineIdPendingDelete,
              ),
            );
          }
          setLineIdPendingDelete(null);
        }}
        open={Boolean(lineIdPendingDelete)}
      />
    </div>
  );
};
